import config from "@Config";
import regions from "@Env/regions";
import { resolveFirebaseFunctionName } from "@Runtime/firebase-function-name";
import { ServiceOrigin } from "@Runtime/runtime-enums";
import * as Sentry from "@sentry/react";
import { getFunctions, httpsCallable } from "firebase/functions";

// Infer io-ts decoded types structurally. Depending on the full codec interface
// makes codecs from the shared package incompatible across io-ts versions.
export type ApiDefinition = { Name: string; Request: { readonly _A: unknown }; Response: { readonly _A: unknown } };
export type ApiRequest<T extends ApiDefinition> = T["Request"]["_A"];
export type ApiResponse<T extends ApiDefinition> = T["Response"]["_A"];

/**
 * Plain typed Firebase transport shared by Query hooks and legacy useApi.
 * Use `await callApi(Api.Users.GetUser, { user_id })` outside React or inside a
 * query/mutation function. It returns the endpoint response unchanged and throws
 * failures. It owns no caching/loading state; components should use useApiQuery
 * for reads or useApiMutation for writes.
 */
export async function callApi<T extends ApiDefinition>(api: T, request: ApiRequest<T>, options?: { timeoutMs?: number }): Promise<ApiResponse<T>> {
  try {
    const region = process.config.FUNCTIONS_ORIGIN === ServiceOrigin.Emulator ? regions.US_CENTRAL_1 : config.region;
    const callable = httpsCallable<ApiRequest<T>, ApiResponse<T>>(getFunctions(undefined, region), resolveFirebaseFunctionName(api.Name), {
      timeout: options?.timeoutMs,
    });
    return (await callable(request)).data;
  } catch (error) {
    const details: Error & { code?: string; status?: string | number } = error instanceof Error ? error : new Error(String(error));
    console.error("API error", api.Name, error);
    Sentry.captureException(error, {
      tags: { type: "call-api", api_name: api.Name, error_status: details.status, error_code: details.code, error_message: details.message },
      fingerprint: [api.Name, details.code, details.status, details.message].filter(value => value !== undefined).map(String),
    });
    void Sentry.flush();
    throw error;
  }
}
